/* eslint-disable react/prop-types */
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableColumn,
  TableHeaderColumn,
} from '@alefeducation/ui-elements-library-core';
import { ProgressCircleHalf } from '@alefeducation/ui-elements-library-icons';
import styled from 'styled-components';
import sortBy from 'lodash/sortBy';
import AssignmentContext from '../../../common/AssignmentContext/AssignmentContext';
import {
  isNcleAlefAssignment,
  getUnassignAllTooltip,
  getUnassignTooltip,
} from '../../../../utils/isNcleAlefAssignment';
import { filterBySelectedTeam } from '../../helpers';
import { renderUnassignIcon } from '../UnassignAllIcon';
import { useStudentListContext } from '../StudentListContext';
import { useUpdateAssignStatusToAssignment } from '../useUpdateAssignStatusToAssignment';
import AssignedContentStatus from '../AssignedContentStatus';
import { NewStudentListSection } from './StudentListSection/NewStudentListSection';
import {
  emptyColumn,
  resubmissionColumn,
  studentColumn,
} from './commonStudentSectionColumns';
import { actionColumnWidth } from './constants';

export const NewPendingSubmissionSection = () => {
  const { t } = useTranslation();
  let {
    groupedSubmissions: { pendingSubmission },
    // eslint-disable-next-line prefer-const
    isLockingOfAssignmentEnabled,
  } = useStudentListContext();
  const {
    assignmentParams: { isNcle },
    assignmentData: { assignmentType },
    filters: { selectedTeamMembersIds, selectedTeamFilter },
  } = useContext(AssignmentContext);

  const isNCLE = isNcleAlefAssignment(isNcle, assignmentType);
  const lockTip = getUnassignTooltip(isNCLE);

  const {
    updatingStatusInProgress,
    onUpdateAssignStatusStudents,
  } = useUpdateAssignStatusToAssignment();

  pendingSubmission =
    pendingSubmission &&
    filterBySelectedTeam(
      selectedTeamFilter,
      selectedTeamMembersIds,
      pendingSubmission,
    );

  const sectionItems =
    pendingSubmission &&
    sortBy(pendingSubmission, ({ student }) => student.name).map(
      ({ student, resubmission }) => ({
        ...student,
        resubmission,
      }),
    );
  const hasStudents = pendingSubmission && pendingSubmission.length > 0;
  const showBulkUnlock = isLockingOfAssignmentEnabled && hasStudents;

  const sectionItemsTable = hasStudents ? (
    <Table
      rows={sectionItems}
      columns={[
        studentColumn(t),
        { ...emptyColumn('320px', true) },
        { ...resubmissionColumn(t) },
        { ...emptyColumn('364px') },
        {
          key: 'id',
          name: t('Actions'),
          renderColumn: ({ row }) => (
            <TableColumn
              row={row}
              isFirstColumn={false}
              className="actions-cell"
            >
              <div className="actions-container">
                {isLockingOfAssignmentEnabled && (
                  <UnassignButtonWrapper
                    onClick={onUpdateAssignStatusStudents(
                      [row],
                      AssignedContentStatus.UnAssigned,
                      true,
                    )}
                    data-testid={`unassign-student-${row.id}`}
                    data-tip={t(lockTip)}
                    className={`data-analytics-${assignmentType
                      .split('_')
                      .join('-')
                      .toLowerCase()}-class-dashboard-single-lock`}
                  >
                    {updatingStatusInProgress ? (
                      <ProgressCircleHalf size={18} />
                    ) : (
                      renderUnassignIcon(isNCLE)
                    )}
                  </UnassignButtonWrapper>
                )}
              </div>
            </TableColumn>
          ),
          renderHeaderColumn: rows => (
            <TableHeaderColumn
              isFirstColumn={false}
              rows={rows}
              width={actionColumnWidth}
            >
              <div className="col-heading">{t('Status')}</div>
            </TableHeaderColumn>
          ),
          alignText: 'right',
          width: actionColumnWidth,
          height: '44px',
        },
      ]}
      isSticky={true}
      translationKeys={t}
    />
  ) : null;

  return (
    <NewStudentListSection
      data-testid="pending-submission-section"
      submissions={pendingSubmission}
      sectionName={t('Pending Submission')}
      labelColor={'#ffffff'}
      groupControlItems={
        <BulkUnlock
          showBulkUnlock={showBulkUnlock}
          students={pendingSubmission}
        />
      }
      sectionItems={sectionItemsTable}
    />
  );
};

export const BulkUnlock = ({ showBulkUnlock, students }) => {
  const {
    updatingStatusInProgress,
    onUpdateAssignStatusStudents,
  } = useUpdateAssignStatusToAssignment();
  const { t } = useTranslation();
  const {
    assignmentParams: { isNcle },
    assignmentData: { assignmentType },
  } = useContext(AssignmentContext);
  const isNCLE = isNcleAlefAssignment(isNcle, assignmentType);
  const lockAllTip = getUnassignAllTooltip(isNCLE);
  if (!showBulkUnlock) return null;

  return (
    <span
      onClick={onUpdateAssignStatusStudents(
        students.map(s => s.student),
        AssignedContentStatus.UnAssigned,
      )}
      data-testid="unassign-all-button"
      data-tip={t(lockAllTip)}
      style={{ display: 'flex' }}
      className={`data-analytics-${assignmentType
        .split('_')
        .join('-')
        .toLowerCase()}-class-dashboard-section-group-lock`}
    >
      {updatingStatusInProgress ? (
        <ProgressCircleHalf fontSize={18} />
      ) : (
        renderUnassignIcon(isNCLE)
      )}
    </span>
  );
};
const UnassignButtonWrapper = styled.div`
  border-radius: 0;
  width: 82px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: flex-end;

  &:hover {
    cursor: pointer;
  }
`;
